import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Download, FileCheck2, Info, Plus, RotateCw, Send, Signature, Trash2 } from "lucide-react";
import { useMemo, useReducer, useState, type ReactNode } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg } from "../api/org";
import type { MessageKey } from "../i18n/keys";
import {
  cancellable,
  ccProblem,
  EXPIRY_CHOICES,
  expiryText,
  FIELD_LABELS,
  FIELD_NAMES,
  fieldBox,
  initialState,
  layoutFields,
  MAX_CC,
  MAX_SIGNERS,
  PROVIDER,
  RECIPIENT_LABELS,
  recipientTone,
  reduceDialog,
  REMINDER_CHOICES,
  requestBody,
  sendButton,
  SIGNING_LANGUAGES,
  signedCopyKey,
  signerLabel,
  STATUS_LABELS,
  statusTone,
  validate,
  VERIFICATION_CHOICES,
  type EsignStatus,
  type Field as SignField,
  type RecipientStatus,
  type Role,
  type SignedCopy,
  type Verification,
} from "../lib/esign";
import { useT } from "../i18n/I18nProvider";
import { Alert, Badge, Button, ChipsInput, Field, Modal, Select, Spinner, TextInput, cx, useToast } from "./ui";
import "./esign.css";

export interface EsignTarget {
  orgId: string;
  publicQuoteId?: string | null;
  designId?: string | null;
}

function targetQuery(t: EsignTarget) {
  return t.publicQuoteId ? { public_quote_id: t.publicQuoteId } : { design_id: t.designId ?? undefined };
}

export function useEnvelopes(t: EsignTarget) {
  return useQuery({
    queryKey: ["esign", t.orgId, t.publicQuoteId ?? null, t.designId ?? null],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/esign/envelopes", { params: { path: { org_id: t.orgId }, query: targetQuery(t) } })),
    // open requests and a signed copy being finalised are read again while the page is open
    refetchInterval: (q) => (q.state.data?.envelopes.some((e) => e.status === "sent" || e.status === "viewed" || e.signed_copy === "finalising") ? 30_000 : false),
  });
}

/** "Send proposal": opens the signing dialog (Bona Fide Sign). Next to "Download proposal". */
export function SendProposalButton({ target, disabled, title }: { target: EsignTarget; disabled?: boolean; title?: string }) {
  const canEdit = useOrg().can("designs_leads", "edit");
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        icon={<Signature />}
        disabled={disabled || !canEdit}
        onClick={() => setOpen(true)}
        title={!canEdit ? "Your role can view this proposal but not send it" : title ?? `Send the proposal for e-signature with ${PROVIDER}`}
        aria-haspopup="dialog"
      >
        Send proposal
      </Button>
      {open && <SendProposalDialog target={target} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The latest signing request under the proposal buttons: its status, each signer's (with "Resend link" for whoever
 * signs now), the signed copy once everyone signed, and Cancel. */
export function EsignStatusLine({ target }: { target: EsignTarget }) {
  const t = useT();
  const envelopes = useEnvelopes(target);
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = useOrg().can("designs_leads", "edit");
  const latest = envelopes.data?.envelopes[0];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["esign", target.orgId] });
    qc.invalidateQueries({ queryKey: ["lead-activity", target.orgId] });
  };
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/orgs/{org_id}/esign/envelopes/{envelope_id}/cancel", { params: { path: { org_id: target.orgId, envelope_id: id } } })),
    onSuccess: () => {
      refresh();
      toast("Signing request cancelled");
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const resend = useMutation({
    mutationFn: (v: { envelope: string; recipient: string }) =>
      unwrap(api.POST("/v1/orgs/{org_id}/esign/envelopes/{envelope_id}/recipients/{recipient_id}/resend", { params: { path: { org_id: target.orgId, envelope_id: v.envelope, recipient_id: v.recipient } } })),
    onSuccess: (envelope, v) => {
      refresh();
      toast(t("esign.status.resent", { name: envelope.recipients.find((r) => r.id === v.recipient)?.name ?? "" }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const download = useMutation({
    mutationFn: (v: { envelope: string; document: "signed" | "certificate" }) =>
      unwrap(api.GET("/v1/orgs/{org_id}/esign/envelopes/{envelope_id}/documents/{document}", { params: { path: { org_id: target.orgId, envelope_id: v.envelope, document: v.document } } })),
    onSuccess: (link) => window.location.assign(link.url),
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!latest) return null;
  const status = latest.status as EsignStatus;
  const copy = (latest.signed_copy ?? "none") as SignedCopy;
  const copyKey = signedCopyKey(copy);
  return (
    <div className="es-status small" aria-live="polite">
      <div className="es-status-head">
        <Signature size={13} className="faint" />
        <span>E-signature</span>
        <Badge tone={statusTone(status)}>{STATUS_LABELS[status]}</Badge>
        {canEdit && cancellable(status) && (
          <button type="button" className="link-btn es-status-cancel" disabled={cancel.isPending} onClick={() => cancel.mutate(latest.id)}>
            {status === "draft" ? "Discard draft" : "Cancel request"}
          </button>
        )}
      </div>
      {status !== "draft" && (
        <ul className="es-signer-list">
          {latest.recipients.map((r) => {
            const rs = r.status as RecipientStatus;
            return (
              <li key={r.id}>
                <span className="es-signer-name ellipsis">{r.name}</span>
                <Badge tone={recipientTone(rs)} plain>{RECIPIENT_LABELS[rs]}</Badge>
                {r.declined_reason && <span className="muted es-signer-note">“{r.declined_reason}”</span>}
                {canEdit && r.can_resend && (
                  <button
                    type="button"
                    className="link-btn es-resend"
                    disabled={resend.isPending}
                    title={t("esign.status.resendTitle", { name: r.name })}
                    onClick={() => resend.mutate({ envelope: latest.id, recipient: r.id })}
                  >
                    <RotateCw size={12} /> {t("esign.status.resend")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {copyKey && (
        <div className="es-copy muted">
          {copy === "finalising" ? <span className="es-dot" aria-hidden /> : <Info size={13} />}
          {t(copyKey)}
        </div>
      )}
      {copy === "ready" && (
        <div className="es-downloads">
          <Button size="sm" icon={<Download />} loading={download.isPending && download.variables?.document === "signed"} disabled={download.isPending} onClick={() => download.mutate({ envelope: latest.id, document: "signed" })}>
            {t("esign.status.downloadSigned")}
          </Button>
          <Button size="sm" icon={<FileCheck2 />} loading={download.isPending && download.variables?.document === "certificate"} disabled={download.isPending} onClick={() => download.mutate({ envelope: latest.id, document: "certificate" })}>
            {t("esign.status.downloadCertificate")}
          </Button>
        </div>
      )}
    </div>
  );
}

const SIGNER_CLASS = ["es-s0", "es-s1", "es-s2", "es-s3", "es-s4", "es-s5"];
const LANGUAGE_OPTIONS = SIGNING_LANGUAGES.map((l) => ({ value: l.code, label: l.name }));

/** The dialog: recipients (the homeowner prefilled, co-signers) with their language and identity check, the signing
 * order, the expiry, reminders, copies of the signed proposal, "keep me updated", a message, and a preview of the
 * proposal's pages with each signer's fields where the acceptance page has its boxes. While Bona Fide Sign is not
 * switched on, the final button says so and "Save draft" keeps the request. */
export function SendProposalDialog({ target, onClose }: { target: EsignTarget; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const preview = useQuery({
    queryKey: ["esign-preview", target.orgId, target.publicQuoteId ?? null, target.designId ?? null],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/esign/preview", { params: { path: { org_id: target.orgId }, query: targetQuery(target) } })),
    staleTime: 60_000,
    retry: false,
  });
  const envelopes = useEnvelopes(target);
  if (preview.isLoading || envelopes.isLoading)
    return (
      <Modal title="Send proposal for signature" onClose={onClose} width={520}>
        <Spinner label="Preparing the proposal…" />
      </Modal>
    );
  if (preview.error || !preview.data)
    return (
      <Modal title="Send proposal for signature" onClose={onClose} width={520} footer={<Button onClick={onClose}>Close</Button>}>
        <Alert tone="danger" title="Could not prepare the proposal.">{errorMessage(preview.error)}</Alert>
      </Modal>
    );
  const draft = envelopes.data?.envelopes.find((e) => e.status === "draft") ?? null;
  return (
    <DialogBody
      target={target}
      preview={preview.data}
      draft={draft}
      onClose={onClose}
      onSaved={(envelope, sent) => {
        qc.invalidateQueries({ queryKey: ["esign", target.orgId] });
        qc.invalidateQueries({ queryKey: ["lead-activity", target.orgId] });
        toast(sent ? `Proposal sent for signature to ${envelope.recipients[0]?.name ?? "the homeowner"}` : "Draft saved: it can be sent once signing is switched on");
        onClose();
      }}
    />
  );
}

function DialogBody({ target, preview, draft, onClose, onSaved }: {
  target: EsignTarget;
  preview: S["EsignPreview"];
  draft: S["EsignEnvelope"] | null;
  onClose: () => void;
  onSaved: (envelope: S["EsignEnvelope"], sent: boolean) => void;
}) {
  const t = useT();
  const VERIFICATION_OPTIONS = VERIFICATION_CHOICES.map((v) => ({ value: v.value, label: t(v.label) }));
  const REMINDER_OPTIONS = REMINDER_CHOICES.map((c) => ({ value: c.days === null ? "off" : String(c.days), label: t(c.label) }));
  const [state, dispatch] = useReducer(reduceDialog, undefined, () =>
    // every signer starts in the lead's language, else the company's main language (the server's default_locale)
    initialState({ name: preview.homeowner?.name, email: preview.homeowner?.email, locale: preview.default_locale }, draft ? {
      // the company's signer comes from Branding (the server adds it), never from the dialog
      recipients: draft.recipients.filter((r) => r.role !== "company").map((r) => ({ name: r.name, email: r.email, role: r.role as Role, locale: r.locale, verification: r.verification })),
      message: draft.message,
      signing_order: draft.signing_order,
      expires_in_days: Math.max(1, Math.round((Date.parse(draft.expires_at) - Date.parse(draft.created_at)) / 86_400_000)),
      reminder_interval_days: draft.reminder_interval_days ?? null,
      cc: draft.cc,
      notify_me: draft.notify_me,
    } : null));
  const [touched, setTouched] = useState(false);
  const { errors, ok } = validate(state);
  const availability = preview.availability;
  const send = sendButton(availability, ok);
  const save = useMutation({
    mutationFn: (sendNow: boolean) =>
      unwrap(api.POST("/v1/orgs/{org_id}/esign/envelopes", { params: { path: { org_id: target.orgId } }, body: requestBody(state, { public_quote_id: target.publicQuoteId, design_id: target.designId }, sendNow) })),
    onSuccess: (envelope, sendNow) => onSaved(envelope, sendNow),
  });
  const submit = (sendNow: boolean) => {
    setTouched(true);
    if (ok) save.mutate(sendNow);
  };

  const pageCount = preview.pages.length;
  // Branding › Proposal PDF › Company signature on: the company's signer signs last, after everyone in the dialog
  const company = preview.company_signer ?? null;
  const signers = state.recipients.length + (company ? 1 : 0);
  const layout = useMemo(() => layoutFields(preview.anchors, pageCount, preview.page_size, signers, !!company), [preview, pageCount, signers, company]);
  const acceptance = preview.acceptance_page;
  const companyIndex = state.recipients.length;
  const named = [...state.recipients.map((r, i) => ({ key: r.key, name: r.name || signerLabel(r, i) })), ...(company ? [{ key: "company", name: company.name }] : [])];
  const extraNames = named.map((r, i) => ({ r, i })).filter(({ i }) => layout.fields.some((f) => f.signer === i && f.page === pageCount + 1));

  return (
    <Modal
      title="Send proposal for signature"
      onClose={() => !save.isPending && onClose()}
      width={1080}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button onClick={() => submit(false)} loading={save.isPending && !save.variables} disabled={save.isPending} title="Keeps the request; nothing is sent">
            {draft ? "Update draft" : "Save draft"}
          </Button>
          <Button variant="primary" icon={<Send />} onClick={() => submit(true)} disabled={send.disabled || save.isPending} loading={save.isPending && !!save.variables} title={send.title}>
            {send.label}
          </Button>
        </>
      }
    >
      <div className="es-grid">
        <div className="es-form stack">
          {!availability.can_send && (
            <Alert tone="info" title={availability.provider_enabled ? "Sending is switched off for now." : `${PROVIDER} is coming soon.`}>
              {availability.provider_enabled
                ? "Proposals are not sent to homeowners yet (internal testing). Save the request as a draft; nothing leaves Solar OS."
                : `${PROVIDER} is our built-in e-signature. Prepare the request now and save it as a draft; nothing is sent yet.`}
            </Alert>
          )}
          <section>
            <div className="es-section-head">
              <h4>Recipients</h4>
              <span className="small muted">{state.order === "sequential" ? "Sign in this order" : "Everyone signs at once"}</span>
            </div>
            <ol className="es-recipients">
              {state.recipients.map((r, i) => {
                const err = touched || r.name || r.email ? errors[r.key] : undefined;
                const who = signerLabel(r, i);
                return (
                  <li key={r.key} className={cx("es-recipient", SIGNER_CLASS[i])}>
                    <span className="es-order" aria-hidden>{state.order === "sequential" ? i + 1 : "•"}</span>
                    <div className="es-recipient-body">
                      <div className="es-recipient-head">
                        <b>{who}</b>
                        {r.role === "homeowner" && target.publicQuoteId && preview.homeowner && <span className="small muted">from the lead</span>}
                        <span className="es-recipient-tools">
                          {r.role === "co_signer" && (
                            <>
                              <button type="button" className="icon-btn" aria-label="Move up" title="Sign earlier" disabled={i <= 1} onClick={() => dispatch({ type: "move", key: r.key, by: -1 })}><ArrowUp size={15} /></button>
                              <button type="button" className="icon-btn" aria-label="Move down" title="Sign later" disabled={i === state.recipients.length - 1} onClick={() => dispatch({ type: "move", key: r.key, by: 1 })}><ArrowDown size={15} /></button>
                              <button type="button" className="icon-btn" aria-label={`Remove ${r.name || "co-signer"}`} title="Remove" onClick={() => dispatch({ type: "remove", key: r.key })}><Trash2 size={15} /></button>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="es-recipient-fields">
                        <TextInput aria-label={`${who}: name`} placeholder="Full name" value={r.name} onChange={(e) => dispatch({ type: "edit", key: r.key, field: "name", value: e.target.value })} />
                        <TextInput aria-label={`${who}: email`} type="email" placeholder="name@example.com" value={r.email} onChange={(e) => dispatch({ type: "edit", key: r.key, field: "email", value: e.target.value })} />
                      </div>
                      <div className="es-recipient-options">
                        <label className="es-mini">
                          <span>{t("esign.signer.language")}</span>
                          <Select ariaLabel={t("esign.signer.languageAria", { signer: who })} value={r.locale} onChange={(v) => dispatch({ type: "locale", key: r.key, value: v })} options={LANGUAGE_OPTIONS} />
                        </label>
                        <label className="es-mini" title={t(VERIFICATION_CHOICES.find((v) => v.value === r.verification)!.hint)}>
                          <span>{t("esign.signer.identityCheck")}</span>
                          <Select<Verification> ariaLabel={t("esign.signer.identityCheckAria", { signer: who })} value={r.verification} onChange={(v) => dispatch({ type: "verification", key: r.key, value: v })} options={VERIFICATION_OPTIONS} />
                        </label>
                      </div>
                      {err && <span className="field-error">{err}</span>}
                    </div>
                  </li>
                );
              })}
              {company && (
                <li className={cx("es-recipient", "es-company", SIGNER_CLASS[companyIndex])} aria-label={t("esign.company.label")}>
                  <span className="es-order" aria-hidden>{state.order === "sequential" ? companyIndex + 1 : 2}</span>
                  <div className="es-recipient-body">
                    <div className="es-recipient-head">
                      <b>{t("esign.company.label")}</b>
                      <span className="small muted">{t("esign.company.fixed")}</span>
                    </div>
                    <div className="es-company-who">
                      <span><b>{company.name}</b>{company.title ? `, ${company.title}` : ""}</span>
                      <span className="muted ellipsis">{company.email}</span>
                    </div>
                    <span className="hint">{t("esign.company.hint")}</span>
                  </div>
                </li>
              )}
            </ol>
            <Button variant="ghost" icon={<Plus />} onClick={() => dispatch({ type: "add" })} disabled={state.recipients.length >= MAX_SIGNERS} title={state.recipients.length >= MAX_SIGNERS ? `Up to ${MAX_SIGNERS} signers` : "A spouse or co-owner who also signs"}>
              Add co-signer
            </Button>
          </section>
          <div className="es-row es-row-wrap">
            <Field label="Signing order">
              <div className="segmented" role="radiogroup" aria-label="Signing order">
                <button type="button" role="radio" aria-checked={state.order === "sequential"} className={state.order === "sequential" ? "on" : ""} onClick={() => dispatch({ type: "order", value: "sequential" })}>One after another</button>
                <button type="button" role="radio" aria-checked={state.order === "parallel"} className={state.order === "parallel" ? "on" : ""} onClick={() => dispatch({ type: "order", value: "parallel" })}>All at once</button>
              </div>
            </Field>
            <div className="es-pair">
              <Field label="Expires" hint={`On ${expiryText(state.expiresInDays)}`}>
                <Select
                  ariaLabel="Expires after"
                  value={String(EXPIRY_CHOICES.includes(state.expiresInDays as never) ? state.expiresInDays : 30)}
                  onChange={(v) => dispatch({ type: "expiry", days: Number(v) })}
                  options={EXPIRY_CHOICES.map((d) => ({ value: String(d), label: `${d} days` }))}
                />
              </Field>
              <Field label={t("esign.reminders.label")} hint={t("esign.reminders.hint")}>
                <Select
                  ariaLabel={t("esign.reminders.label")}
                  value={state.reminderDays === null ? "off" : String(state.reminderDays)}
                  onChange={(v) => dispatch({ type: "reminders", days: v === "off" ? null : Number(v) })}
                  options={REMINDER_OPTIONS}
                />
              </Field>
            </div>
          </div>
          <Field label="Message" hint="Shown in the signing email above the link.">
            <textarea className="textarea" rows={3} maxLength={2000} value={state.message} onChange={(e) => dispatch({ type: "message", value: e.target.value })} />
          </Field>
          <Field label={t("esign.copies.label")} hint={t("esign.copies.hint", { max: MAX_CC })} error={touched && errors.cc ? t(errors.cc as MessageKey, { max: MAX_CC }) : undefined}>
            <ChipsInput values={state.cc} onChange={(values) => dispatch({ type: "cc", values })} placeholder={state.cc.length ? "" : "office@example.com"} validate={(v) => {
              const problem = ccProblem(v, state.cc);
              return problem ? t(problem, { max: MAX_CC }) : null;
            }} />
          </Field>
          <label className="es-check">
            <input type="checkbox" checked={state.notifyMe} onChange={(e) => dispatch({ type: "notifyMe", value: e.target.checked })} />
            <span>
              {t("esign.notifyMe.label")}
              <small className="muted">{t("esign.notifyMe.hint")}</small>
            </span>
          </label>
        </div>

        <div className="es-preview" aria-label="Where each person signs">
          <div className="es-preview-head">
            <b className="ellipsis">{preview.document_name}</b>
            <span className="small muted">{pageCount + (layout.extraPage ? 1 : 0)} pages</span>
          </div>
          <div className="es-legend small">
            {state.recipients.map((r, i) => (
              <span key={r.key} className={cx("es-chip", SIGNER_CLASS[i])}>
                <i aria-hidden /> {r.name || signerLabel(r, i)}
              </span>
            ))}
            {company && (
              <span className={cx("es-chip", SIGNER_CLASS[companyIndex])}>
                <i aria-hidden /> {company.name}
              </span>
            )}
          </div>
          <div className="es-pages">
            {acceptance != null && (
              <PageWithFields title={`Page ${acceptance} · Acceptance`} image={preview.pages[acceptance - 1]?.image} pageSize={preview.page_size} fields={layout.fields.filter((f) => f.page === acceptance)} />
            )}
            {layout.extraPage && (
              <PageWithFields title={`Page ${pageCount + 1} · Added`} pageSize={preview.page_size} fields={layout.fields.filter((f) => f.page === pageCount + 1)}>
                <b className="es-extra-title">Additional signatures</b>
                <span className="es-extra-sub">Each person below signs to accept the proposal on the previous pages.</span>
                {extraNames.map(({ r, i }) => {
                  const f = layout.fields.find((x) => x.signer === i && x.kind === "signature" && x.page === pageCount + 1)!;
                  const top = ((preview.page_size[1]! - (f.y + 72) + 10) / preview.page_size[1]!) * 100;
                  return <span key={r.key} className="es-extra-name" style={{ top: `${top}%` }}>{r.name}</span>;
                })}
              </PageWithFields>
            )}
          </div>
          <div className="es-strip" aria-label="All pages">
            {preview.pages.map((p) => (
              <img key={p.page} src={p.image} alt={`Page ${p.page}`} className={cx(p.page === acceptance && "is-acceptance")} loading="lazy" />
            ))}
          </div>
          <p className="small muted es-note">
            <Info size={13} /> The homeowner signs and dates the acceptance page, everyone initials the terms, and co-signers sign on an added page.
            {company && <> {t("esign.company.note")}</>}
          </p>
        </div>
      </div>
      {save.error && (
        <Alert tone="danger" title="Not saved.">
          {errorMessage(save.error)}
        </Alert>
      )}
    </Modal>
  );
}

function PageWithFields({ title, image, pageSize, fields, children }: {
  title: string;
  image?: string;
  pageSize: number[];
  fields: SignField[];
  children?: ReactNode;
}) {
  return (
    <figure className="es-page">
      <figcaption className="small muted">{title}</figcaption>
      <div className="es-page-sheet" style={{ aspectRatio: `${pageSize[0]} / ${pageSize[1]}` }}>
        {image ? <img src={image} alt="" /> : null}
        {children}
        {fields.map((f, i) => (
          <span key={i} className={cx("es-field", `es-${f.kind}`, SIGNER_CLASS[f.signer])} style={fieldBox(f, pageSize)} title={`${FIELD_NAMES[f.kind]} · signer ${f.signer + 1}`}>
            {f.kind === "signature" ? <Signature size={11} /> : null}
            {FIELD_LABELS[f.kind]}
          </span>
        ))}
      </div>
    </figure>
  );
}
